import { useMemo } from "react";
import useSWRInfinite from "swr/infinite";
import { Voc } from "@/models/Voc";
import { defaultSWROption } from "@/services/api/api.service";
import { ConversationService } from "@/services/conversation/conversation.service";

const MESSAGE_VOCS_PAGE_LIMIT = 10;

export const useMessageVocs = (conversationId: string | null, messageId: string | null) => {
  const getVocsKey = (
    pageIndex: number,
    previousPageData: { vocs: Voc[]; hasMore: boolean; total: number; } | null,
  ) => {
    if (!conversationId || !messageId) return null;
    if (previousPageData && !previousPageData.hasMore) return null;

    const offset = pageIndex * MESSAGE_VOCS_PAGE_LIMIT;
    return ["message-vocs", conversationId, messageId, offset, MESSAGE_VOCS_PAGE_LIMIT];
  };

  const {
    data: vocsPages,
    isLoading,
    isValidating: isFetchingMore,
    size: vocsPageSize,
    setSize: setVocsPageSize,
  } = useSWRInfinite(getVocsKey, ConversationService.fetchMessageVocs, defaultSWROption);

  const loadMoreVocs = () => {
    if (!isLoading && hasMoreVocs) {
      setVocsPageSize(vocsPageSize + 1);
    }
  };

  const vocs = useMemo(
    () =>
      vocsPages?.filter((pagedVocs) => !!pagedVocs).flatMap((
        pagedVocs,
      ) => pagedVocs?.vocs),
    [vocsPages],
  );

  const hasMoreVocs = vocsPages?.at(-1)?.hasMore ?? false;
  const total = vocsPages?.[0]?.total ?? 0;

  return {
    vocs,
    hasMore: hasMoreVocs,
    total,
    isLoading,
    isFetchingMore,
    loadMoreVocs,
  };
};
